import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WaCardRow, WaGridRow, WaRow } from './waRows';

/**
 * WA-2 — the WhatsApp conversations grid (PRD §1.2 / design §6). v1 is READ-ONLY and DISPLAY-ONLY:
 * it fetches the server-side projection (`GET /api/whatsapp-grid`), renders it as cards on phone and a
 * table on desktop, and opens a thread on tap. 🔴 There is NO write path in this component — no take-over,
 * no editable pill, no status change (INV-1 / AC-W9; take-over and the triage pill are P5).
 *
 * The five saved views (§6.3) are PRESETS over the endpoint's projection-keyed primitives — each one sets
 * server query params and NEVER re-implements a predicate client-side. That is AC-W6b at the row level: a
 * row can never appear in a view whose predicate contradicts the row's own chip, because the chip and the
 * view read the same projected field off the same response.
 */

export type WaViewKey = 'necesitan' | 'ventana' | 'mios' | 'sin-asignar' | 'todas';
export type WaChannelKey = 'todos' | 'web' | 'whatsapp';
export type WaSortKey = 'recent' | 'wait' | 'window';

interface WaView {
  key: WaViewKey;
  labelKey: string;
  params: Record<string, string>;
  /** A view whose predicate IS "work that needs you" renders its empty as SUCCESS (AC-W11); a view that
   *  merely narrows scope renders it as a filter with an escape hatch (AC-W12). */
  workQueue: boolean;
  emptyTitleKey?: string;
  emptyBodyKey?: string;
}

// 🔴 The view table is MODULE-LEVEL, where no `t` exists, so it carries translation KEYS and the render
// resolves them. Keeping the labels as literals here is what made this pane a Spanish island.
export const WA_VIEWS: WaView[] = [
  { key: 'necesitan', labelKey: 'waGrid.views.necesitan', params: { estado: 'necesita_respuesta' }, workQueue: true, emptyTitleKey: 'waGrid.empty.caughtUpTitle', emptyBodyKey: 'waGrid.empty.caughtUpBody' },
  { key: 'ventana', labelKey: 'waGrid.views.ventana', params: { windowClosing: 'true' }, workQueue: true, emptyTitleKey: 'waGrid.empty.windowCaughtUpTitle', emptyBodyKey: 'waGrid.empty.windowCaughtUpBody' },
  { key: 'mios', labelKey: 'waGrid.views.mios', params: { owner: 'mine' }, workQueue: false },
  { key: 'sin-asignar', labelKey: 'waGrid.views.sinAsignar', params: { owner: 'unassigned' }, workQueue: false },
  { key: 'todas', labelKey: 'waGrid.views.todas', params: {}, workQueue: false },
];

// Each sort axis has ONE sensible direction: the longest wait first, the nearest deadline first. v1 does not
// offer a direction toggle — an axis whose useful reading is one-directional does not need one.
const SORT_DIR: Record<Exclude<WaSortKey, 'recent'>, 'asc' | 'desc'> = { wait: 'desc', window: 'asc' };
const SORT_LABEL_KEY: Record<WaSortKey, string> = { recent: 'waGrid.sort.recent', wait: 'waGrid.sort.wait', window: 'waGrid.sort.window' };

export type WaEmptyKind = 'unknown' | 'not-connected' | 'nothing-yet' | 'caught-up' | 'filtered';

/**
 * 🔴 The connection probe is a THREE-answer question, so it gets a three-answer type (AC-W14b). It used to
 * be `boolean | null` where null meant BOTH "not asked yet" and "the ask failed" — and the failure path was
 * swallowed (a silent `return` on !res.ok, an empty catch), which made "we could not find out" and "it is
 * fine" indistinguishable. A fail-soft that hides its own failure is not soft, it is quiet.
 */
export type ConnState = 'resolving' | 'connected' | 'disconnected' | 'unknown';

/**
 * 🔴 The four empty states are resolved by ONE total function, so they are mutually exclusive by
 * construction — "conflating them IS the bug" (PRD §4). Order matters and each branch is a different
 * TRUTH, not a different wording:
 *   not-connected  a configuration fault — we cannot see the messages (AC-W14). Never "you have none".
 *   nothing-yet    the account has never had a conversation — onboarding, not congratulation (AC-W13).
 *   caught-up      a work-queue view legitimately holding nothing — success (AC-W11).
 *   filtered       data exists, the current narrowing excludes it — needs an escape hatch (AC-W12).
 */
export function resolveEmptyKind(args: {
  channel: WaChannelKey;
  view: WaViewKey;
  conn: ConnState;
  waTotal: number;
  webTotal: number;
  /** 🔴 D7: DURABLE history for the account, from the DB — not what is currently visible. */
  conversationsEver: number;
}): WaEmptyKind {
  const { channel, view, conn, waTotal, webTotal, conversationsEver } = args;
  // 🔴 AC-W14b: NOT KNOWING is not the same as knowing there is nothing. An unknown connection must render
  // as unknown — never `nothing-yet` (D6's lie reached through the error path) and never `not-connected`
  // (crying wolf over a transient blip). Same vocabulary as AC-W15: a screen that cannot confirm stops claiming.
  if (channel !== 'web' && conn === 'unknown') return 'unknown';
  // 🔴 D6: gate on whether the current scope INCLUDES WhatsApp, not on the operator having gone and
  // clicked the WhatsApp chip. Gating on `channel === 'whatsapp'` meant the DEFAULT view — the only screen
  // he actually lands on — told an unconnected account "cuando alguien te escriba… aparece acá", which is
  // the precise lie AC-W14 forbids. The old AC passed only because the spec pre-clicked the filter.
  if (channel !== 'web' && conn === 'disconnected') return 'not-connected';
  // 🔴 D7: "nothing yet" is a claim about HISTORY, so it may only be made from the durable count. Deciding
  // it from what is on screen (`waTotal + webTotal`, where webTotal comes from the live socket roster) told
  // an account with real history that it had never had a conversation, purely because nobody was online.
  if (conversationsEver === 0) return 'nothing-yet';
  if (waTotal + webTotal === 0) return 'filtered';
  const v = WA_VIEWS.find((x) => x.key === view);
  if (v?.workQueue && channel !== 'web') return 'caught-up';
  return 'filtered';
}

interface ApiRow {
  id: string; visitorId: string; contact: string; phone: string | null;
  lastMessage: string; lastMessageAt: string | null;
  estado: 'necesita_respuesta' | 'esperando_al_cliente';
  owner: { kind: 'human' | 'bot_liberada' | 'bot'; name: string | null };
  waitMs: number | null; windowExpiresAt: string | null; windowClosing: boolean; windowTier: 'none' | 'amber' | 'rose'; operationalStatus: string;
}

const POLL_MS = 15000;

/** Identity + rendered-content comparison. Two payloads that render identically must NOT offer a refresh —
 *  an affordance that appears when nothing changed is its own kind of noise. */
// 🔴 Compare FACTS, not the clock. `waitMs` is elapsed time and is different on every single poll, so
// including it made every poll look like a change and the pane offered "refresh" forever — my own control
// caught that. The underlying fact is `lastMessageAt`, which is stable until a real message arrives; the
// wait LABEL still ticks in place off it (UXeng's point 6: ticks may relabel, never re-rank).
const rowFingerprint = (r: ApiRow) =>
  [r.id, r.estado, r.owner?.kind, r.owner?.name, r.lastMessageAt, r.windowExpiresAt, r.windowTier, r.operationalStatus, r.lastMessage, r.contact].join('|');
const sameRows = (a: ApiRow[], b: ApiRow[]) =>
  a.length === b.length && a.every((r, i) => rowFingerprint(r) === rowFingerprint(b[i]));
const sameWeb = (a: WaRow[], b: WaRow[]) =>
  a.length === b.length && a.every((r, i) => r.key === b[i].key && r.contact === b[i].contact && r.preview === b[i].preview);

/**
 * 🔴 D1 — card columns follow the CONTAINER, not the viewport. `md:grid-cols-2` is a VIEWPORT breakpoint:
 * it is unconditionally on at >=1024, including when the pane is a 255px sidebar, where it produced two
 * 116px cards with names clipped to "A." and chips cut mid-word. Tailwind's default scale has no
 * container query, and §9 forbids inventing breakpoint values — so the compact pane simply never asks
 * for two columns. The width that matters is the pane's, and only the pane knows it.
 */
const cardGridCls = (compact: boolean) => `grid gap-2 p-2 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`;

export const WaConversationsGrid: React.FC<{
  /** Web conversations, from the DURABLE read merged with the live roster. They carry a real
   *  lastMessageAt, ESTADO and owner, so they sort and filter alongside WhatsApp rows instead of being
   *  appended below them. Only the 24h window is genuinely absent on web. */
  webRows: WaRow[];
  /** Needed to answer "Míos" for web rows client-side — see WaOwnerChip.agentId. */
  currentUserId?: string | null;
  selectedKey?: string | null;
  onOpen: (row: WaRow) => void;
  onConnectWhatsApp: () => void;
  /** The pane is a narrow sidebar beside an open thread rather than the full-width grid. The TABLE is
   *  the expanded shape (§5.3); in a 384px column its eight columns would be a horizontal scroll inside
   *  a vertical one — the exact shape §8-A/G reject. So a compact pane renders CARDS at any width. */
  compact?: boolean;
}> = ({ webRows, currentUserId = null, selectedKey, onOpen, onConnectWhatsApp, compact = false }) => {
  const { t } = useTranslation('common');
  const [view, setView] = useState<WaViewKey>('todas');
  const [channel, setChannel] = useState<WaChannelKey>('todos');
  const [sort, setSort] = useState<WaSortKey>('recent');

  // 🔴 STABLE WHILE READ (Workstream B). `apiRows` is what is ON SCREEN; `pendingRows` is what the poll
  // last fetched. Live data is a feature; the screen moving under the operator is not — so a poll NEVER
  // replaces rows in place. It fills the buffer, the pane offers "N nuevas — actualizar", and HE decides
  // when the list changes.
  const [apiRows, setApiRows] = useState<ApiRow[]>([]);
  const [pendingRows, setPendingRows] = useState<ApiRow[] | null>(null);
  // Web rows arrive from the live socket roster on their own cadence, so they are snapshotted too —
  // otherwise a visitor closing a tab makes a row VANISH mid-read, which is the most literal "flaky"
  // there is. They enter the same buffer and land only on apply.
  const [shownWebRows, setShownWebRows] = useState<WaRow[]>([]);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [waTotal, setWaTotal] = useState(0);
  const [conversationsEver, setConversationsEver] = useState(0);
  const [loaded, setLoaded] = useState(false);   // a successful fetch has landed at least once
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);     // nothing to show AND the fetch failed (AC-W17)
  const [stale, setStale] = useState(false);     // we HAVE data and the refresh failed (AC-W15)
  const [conn, setConn] = useState<ConnState>('resolving');
  const [connFailures, setConnFailures] = useState(0); // 🔴 OBSERVABLE: the swallow is what made this invisible
  const [nowMs, setNowMs] = useState(() => Date.now());

  const loadedRef = useRef(false);
  useEffect(() => { loadedRef.current = loaded; }, [loaded]);
  // 🔴 STALE-RESPONSE GUARD (the view-switch race). Two fetches can be in flight at once — the initial
  // mount's 'todas' request and a view-tab click's filtered request, issued moments later — and HTTP gives
  // no ordering guarantee on which resolves first. `applyNextRef` below is meant to force-apply the ONE
  // response that answers the view switch; without this counter, whichever request resolves first consumes
  // that flag (line ~230) regardless of which view it was answering, so the filtered response can arrive
  // second, find the flag already spent, and get silently buffered into `pendingRows` — the screen keeps
  // showing the unfiltered set the view tab was supposed to replace. Each fetchRows() call claims the next
  // id; a response only commits if it is still the LATEST outstanding request when it lands.
  const requestIdRef = useRef(0);
  // the fetch closure reads these; refs keep it out of the dependency list so a poll never re-subscribes
  const apiRowsRef = useRef<ApiRow[]>([]);
  useEffect(() => { apiRowsRef.current = apiRows; }, [apiRows]);
  const webRowsRef = useRef<WaRow[]>(webRows);
  useEffect(() => { webRowsRef.current = webRows; }, [webRows]);
  const shownWebRowsRef = useRef<WaRow[]>([]);
  useEffect(() => { shownWebRowsRef.current = shownWebRows; }, [shownWebRows]);
  /**
   * 🔴 ADDITIONS LAND, REMOVALS WAIT. Buffering web rows wholesale was wrong in one direction: a brand-new
   * live chat is the operator's actual job arriving, and it must not sit behind a refresh click — that
   * regressed auto_respond and inference_degradation, which take a chat and expect to see it.
   * Disappearance is the unstable half (a visitor closes a tab and a row he was reading evaporates), so
   * only removals wait for an explicit apply. New rows are appended after the projected ones, so they
   * cannot displace anything already on screen.
   */
  useEffect(() => {
    setShownWebRows((prev) => {
      const byKey = new Map(prev.map((r) => [r.key, r] as const));
      for (const r of webRows) byKey.set(r.key, r);   // additions + updates land immediately
      return Array.from(byKey.values());              // removals persist until applyPending()
    });
  }, [webRows]);
  const consecutiveFailuresRef = useRef(0);
  useEffect(() => { consecutiveFailuresRef.current = consecutiveFailures; }, [consecutiveFailures]);

  const queryString = useMemo(() => {
    const v = WA_VIEWS.find((x) => x.key === view)!;
    const p = new URLSearchParams(v.params);
    if (sort !== 'recent') { p.set('sortBy', sort); p.set('sortDir', SORT_DIR[sort]); }
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [view, sort]);

  const fetchRows = useCallback(async () => {
    const myRequestId = ++requestIdRef.current;
    setRefreshing(true);
    try {
      const res = await fetch(`/api/whatsapp-grid${queryString}`);
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      // A newer fetchRows() (a later view/channel/sort click, or the next poll tick) has already been
      // issued — this response is answering a query nobody is asking anymore. Applying it would mean
      // whichever request happens to land LAST wins, not whichever the user most recently asked for.
      if (myRequestId !== requestIdRef.current) return;
      // 🔴 AC-W16: rows are replaced ONLY on success. A failed or in-flight refetch never blanks what is
      // already on screen — losing the list (and the scroll position) on a filter tap is the failure mode.
      const next: ApiRow[] = Array.isArray(data.conversations) ? data.conversations : [];
      setWaTotal(typeof data.total === 'number' ? data.total : 0);
      setConversationsEver(typeof data.conversationsEver === 'number' ? data.conversationsEver : 0);
      setConsecutiveFailures(0);
      setError(false);
      setStale(false);
      if (!loadedRef.current || applyNextRef.current) {
        applyNextRef.current = false;
        // first paint: there is nothing to disturb, so show it
        setApiRows(next);
        setShownWebRows(webRowsRef.current);
        setPendingRows(null);
        setLoaded(true);
      } else {
        // afterwards: buffer it. Identical payloads must not offer a pointless "refresh".
        setPendingRows(sameRows(next, apiRowsRef.current) && sameWeb(webRowsRef.current, shownWebRowsRef.current) ? null : next);
      }
    } catch {
      if (myRequestId !== requestIdRef.current) return; // superseded — see the success-path guard above
      // A failed refresh with data on screen is STALENESS, not an error: the list is still usable, it has
      // just stopped claiming to be live (AC-W15). With nothing on screen it IS an error — rendering it as
      // "caught up" would tell the operator nothing needs them during an outage (AC-W17).
      // 🔴 TOLERATE ONE FAILURE. A single dropped request is normal on mobile signal; announcing
      // staleness on it made the banner flap, and each flap shoved every row 35px under his thumb.
      // Two consecutive failures is a real loss of liveness; one is weather.
      const fails = consecutiveFailuresRef.current + 1;
      setConsecutiveFailures(fails);
      if (loadedRef.current) { if (fails >= 2) setStale(true); } else setError(true);
    } finally {
      setRefreshing(false);
    }
  }, [queryString]);

  const fetchConnection = useCallback(async () => {
    try {
      const res = await fetch('/api/whatsapp/connection-status');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setConn(data.connected ? 'connected' : 'disconnected');
      setConnFailures(0);
    } catch (e: any) {
      // 🔴 The failure is RECORDED, not swallowed: a distinct state, a counter, and a log line. Previously a
      // silent `return` and an empty catch left `null`, so a failed probe and a pending one were the same
      // value — and a genuinely dark channel hid behind the error path.
      setConn('unknown');
      setConnFailures((n) => n + 1);
      console.error('[wa-grid] connection-status probe failed — connection state is UNKNOWN:', e?.message ?? e);
    }
  }, []);

  useEffect(() => { void fetchRows(); }, [fetchRows]);
  useEffect(() => { void fetchConnection(); }, [fetchConnection]);
  useEffect(() => {
    const t = setInterval(() => { void fetchRows(); }, POLL_MS);
    return () => clearInterval(t);
  }, [fetchRows]);
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000); // keep the countdown honest without re-fetching
    return () => clearInterval(t);
  }, []);

  // Render ONE tier at a time (lg: = table, else cards) rather than CSS-hiding a duplicate — a hidden
  // duplicate row would double every data-testid (the LeadsGrid precedent).
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const waRows: WaRow[] = useMemo(() => apiRows.map((r) => ({
    key: r.id, conversationId: r.id, visitorId: r.visitorId, contact: r.contact, phone: r.phone,
    preview: r.lastMessage, lastMessageAt: r.lastMessageAt, channel: 'whatsapp' as const,
    estado: r.estado, owner: r.owner, waitMs: r.waitMs, windowExpiresAt: r.windowExpiresAt,
    windowClosing: !!r.windowClosing, windowTier: r.windowTier ?? 'none', operationalStatus: r.operationalStatus,
  })), [apiRows]);

  const rows: WaRow[] = useMemo(() => {
    const wa = channel === 'web' ? [] : waRows;

    /**
     * 🔴 EVERY VIEW'S SEMANTICS APPLY TO WEB TOO — except the one that genuinely cannot.
     *
     * This used to read `view !== 'todas' ? [] : shownWebRows`: web rows were dropped wholesale from four
     * of the five views, silently, with no count and no hint that anything had been hidden. The reasoning
     * was that a view keyed on the WhatsApp projection cannot hold a row without one — but "needs a
     * reply", "mine" and "unassigned" are not WhatsApp ideas, they are conversation ideas, and web rows
     * now carry the facts to answer them. Only "ventana por cerrar" is truly WhatsApp-only: the 24h
     * session window does not exist on web, so a web row can neither satisfy nor contradict it.
     */
    const webPasses = (r: WaRow): boolean => {
      switch (view) {
        case 'todas': return true;
        case 'necesitan': return r.estado === 'necesita_respuesta';
        case 'mios': return !!currentUserId && r.owner?.agentId === currentUserId;
        case 'sin-asignar': return !r.owner?.agentId;
        case 'ventana': return false;   // the 24h WhatsApp window — web has none
        default: return true;
      }
    };
    const web = channel === 'whatsapp' ? [] : shownWebRows.filter(webPasses);

    /**
     * 🔴 SORTED TOGETHER, NOT CONCATENATED. `[...wa, ...web]` pinned every web row below every WhatsApp
     * row, permanently — so the newest web conversation, the thing most likely to need someone, landed at
     * the BOTTOM of an account with a dozen WhatsApp threads. It was in the DOM and invisible in practice,
     * which is the same thing as missing.
     *
     * Only the recency axis can order the two channels: `wait` and `window` are WhatsApp-only readings
     * (the endpoint sorts those server-side and web rows have no such values), so under those the WhatsApp
     * order is preserved and web rows follow — explicitly, rather than as a side effect of concatenation.
     */
    if (sort !== 'recent') return [...wa, ...web];
    const at = (r: WaRow) => (r.lastMessageAt ? Date.parse(r.lastMessageAt) : 0);
    return [...wa, ...web].sort((a, b) => at(b) - at(a));
  }, [waRows, shownWebRows, channel, view, sort, currentUserId]);

  /**
   * 🔴 APPLY ON HIS COMMAND, ANCHORED TO WHAT HE IS READING. Restoring raw scrollTop is not enough: rows
   * above the reading position can change height or count, which slides the row under his eyes anyway.
   * So anchor to the FIRST VISIBLE ROW and restore ITS offset within the viewport — the row he is looking
   * at stays exactly where it was, and the list rearranges around it.
   */
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  /**
   * 🔴 MF-1(a). The view strip scrolls (scrollW 460 > clientW 389 at 390px) and starts at scrollLeft 0, so
   * the SELECTED tab sat at x=412..452 — entirely off-screen at mount. Every tab the operator could see
   * rendered unselected, which is why he could not tell which view he was in. Bring the selected one into
   * view on mount and whenever it changes.
   */
  const viewStripRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = viewStripRef.current?.querySelector<HTMLElement>(`[data-testid="wa-view-${view}"]`);
    el?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [view]);
  const applyPending = () => {
    const box = scrollBoxRef.current;
    let anchorKey: string | null = null;
    let anchorOffset = 0;
    if (box) {
      const boxTop = box.getBoundingClientRect().top;
      const el = Array.from(box.querySelectorAll<HTMLElement>('[data-wa-row]'))
        .find((n) => n.getBoundingClientRect().bottom > boxTop);
      if (el) { anchorKey = el.getAttribute('data-wa-row'); anchorOffset = el.getBoundingClientRect().top - boxTop; }
    }
    if (pendingRows) setApiRows(pendingRows);
    setShownWebRows(webRowsRef.current);
    setPendingRows(null);
    if (box && anchorKey) {
      requestAnimationFrame(() => {
        const again = box.querySelector<HTMLElement>(`[data-wa-row="${CSS.escape(anchorKey!)}"]`);
        if (again) box.scrollTop += (again.getBoundingClientRect().top - box.getBoundingClientRect().top) - anchorOffset;
      });
    }
  };
  const pendingCount = pendingRows ? Math.max(0, pendingRows.length - apiRows.length) : 0;

  const filtersActive = view !== 'todas' || channel !== 'todos' || sort !== 'recent';
  const clearFilters = () => { setView('todas'); setChannel('todos'); setSort('recent'); };
  // A view/sort/channel change is HIS action, so the fetch it triggers lands DIRECTLY — he asked for the
  // list to change. Only unrequested background churn goes through the buffer.
  // 🔴 NOT by clearing `loaded`: that shows skeletons, which is blanking, which is exactly what AC-W16
  // forbids ("a filter tap must not blank the list"). The flag changes where the next payload GOES,
  // without ever taking the current rows off screen.
  const applyNextRef = useRef(false);
  useEffect(() => { applyNextRef.current = true; setPendingRows(null); }, [view, channel, sort]);

  const emptyKind = resolveEmptyKind({ channel, view, conn, waTotal, webTotal: shownWebRows.length, conversationsEver });
  const activeView = WA_VIEWS.find((x) => x.key === view)!;

  // ── chrome ───────────────────────────────────────────────────────────────────────────────────────
  const tabCls = (active: boolean) =>
    `px-3 min-h-[44px] text-[13px] font-semibold whitespace-nowrap border-b-2 ${active ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text-strong'}`;
  // 🔴 MF-1(b): the VIEW is the primary axis, so the filled treatment belongs to it alone. The channel
  // row's filled-blue "All" was the loudest thing in the header and read AS the view — with two "All"s on
  // screen and the wrong one highlighted. Channel and sort drop to an outline, and the channel row gets a
  // "Channel" label exactly as sort already had one, so neither pill can be mistaken for the view.
  const chipCls = (active: boolean) =>
    `inline-flex items-center min-h-[36px] px-2 text-[11px] font-medium rounded-md border ${active ? 'border-primary/50 text-primary' : 'border-transparent text-text-muted hover:bg-canvas'}`;

  const header = (
    <div className="border-b border-border-neutral bg-surface">
      <div ref={viewStripRef} className="flex items-center gap-1 overflow-x-auto px-2" role="tablist" aria-label={t('waGrid.a11y.views')}>
        {WA_VIEWS.map((v) => (
          <button key={v.key} type="button" role="tab" aria-selected={view === v.key}
            data-testid={`wa-view-${v.key}`} className={tabCls(view === v.key)} onClick={() => setView(v.key)}>
            {t(v.labelKey)}
          </button>
        ))}
      </div>
      {/* 🔴 MF-4: channel and sort share ONE row (they were two, and wrapped to three at 390px). With the
          status strip overlaid rather than reserved, the chrome above the list drops by ~80px and the first
          conversation lands in the top third instead of at y=265 of a 664px viewport. */}
      <div data-testid="wa-filter-row" className="flex items-center gap-1 px-2 pb-1 overflow-x-auto whitespace-nowrap">
        <span className="text-[11px] text-text-muted shrink-0">{t('waGrid.channels.label')}</span>
        {(['todos', 'web', 'whatsapp'] as WaChannelKey[]).map((c) => (
          <button key={c} type="button" data-testid={`wa-channel-${c}`} aria-pressed={channel === c}
            className={`${chipCls(channel === c)} shrink-0`} onClick={() => setChannel(c)}>
            {t(`waGrid.channels.${c}`)}
          </button>
        ))}
        <span className="text-[11px] text-text-muted shrink-0 ms-2">{t('waGrid.sort.label')}</span>
        {(['recent', 'wait', 'window'] as WaSortKey[]).map((s) => (
          <button key={s} type="button" data-testid={`wa-sort-${s}`} aria-pressed={sort === s}
            className={`${chipCls(sort === s)} shrink-0`} onClick={() => setSort(s)}>{t(SORT_LABEL_KEY[s])}</button>
        ))}
      </div>
      {/* the refresh pulse OVERLAYS (absolute) rather than occupying layout — as a block element it was a
          2px transient that moved every row each poll. */}
      {refreshing && loaded && <div data-testid="wa-refreshing" className="relative"><span className="absolute inset-x-0 top-0 h-0.5 bg-primary/40 animate-pulse" /></div>}
    </div>
  );


  /**
   * 🔴 MF-3 — the transients live in a bottom-anchored OVERLAY, not a reserved row.
   *
   * They used to sit in a fixed 36px strip so that a banner appearing could not move rows (flaky cause 1b:
   * a dropped poll shoved every row 35px mid-read). That guarantee is right and stays — but on a 664px
   * phone the strip was empty in the common case and cost 36px permanently, reading as a dead white band
   * and pushing the first conversation to y=265. Zero-height-when-empty would have traded the guarantee
   * back for the nit, so instead: absolute, so it occupies NO layout height AND cannot reflow the list.
   * Bottom-anchored because it is thumb-reachable, it covers the least valuable part of the list, and the
   * "N new" affordance is meant to be noticed. b4 (no row shift) is the guardrail on this mechanism change.
   */
  const statusOverlay = (
    <div data-testid="wa-status-strip" className="absolute inset-x-0 bottom-2 z-30 mx-3 flex items-center gap-2 pointer-events-none [&>*]:pointer-events-auto empty:hidden">
      {stale ? (
        <div data-testid="wa-stale-banner" role="status" className="flex items-center gap-2 px-3 py-2 rounded-lg shadow-md border border-border-neutral bg-surface text-[12px] text-opt-amber">
          <span>{t('waGrid.stale.text')}</span>
          <button type="button" data-testid="wa-refresh" className="underline font-semibold" onClick={() => { void fetchRows(); void fetchConnection(); }}>{t('waGrid.stale.refresh')}</button>
        </div>
      ) : pendingRows ? (
        <button type="button" data-testid="wa-apply-pending" onClick={applyPending}
          className="flex items-center gap-2 px-3 py-2 rounded-lg shadow-md border border-primary/30 bg-surface text-[12px] font-semibold text-primary">
          {pendingCount > 0 ? t('waGrid.pending.countLabel', { count: pendingCount }) : t('waGrid.pending.changesLabel')}
        </button>
      ) : conn === 'disconnected' && channel !== 'web' && rows.length > 0 ? (
        <div data-testid="wa-not-connected-banner" role="status" className="flex items-center gap-2 px-3 py-2 rounded-lg shadow-md border border-border-neutral bg-surface text-[12px] text-text-strong">
          <span aria-hidden>⚠</span>
          <span>{t('waGrid.banner.notConnected')}</span>
          <button type="button" data-testid="wa-connect-banner-link" className="inline-flex items-center px-2 underline font-semibold text-primary" onClick={onConnectWhatsApp}>{t('waGrid.banner.connect')}</button>
        </div>
      ) : null}
    </div>
  );

  // ── skeletons (AC-W16): the CARD ANATOMY, not a spinner — the layout must not jump when data lands ──
  const skeletons = (
    <div className={cardGridCls(compact)}>
      {[0, 1, 2].map((i) => (
        <div key={i} data-testid="wa-skeleton" className="flex gap-3 p-3 rounded-xl bg-surface border border-border-neutral animate-pulse">
          <span className="w-[38px] h-[38px] rounded-xl bg-canvas shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="h-3 w-1/3 bg-canvas rounded" />
            <div className="h-3 w-2/3 bg-canvas rounded" />
            <div className="flex gap-2"><div className="h-6 w-28 bg-canvas rounded-md" /><div className="h-6 w-16 bg-canvas rounded-md" /></div>
          </div>
        </div>
      ))}
    </div>
  );

  const emptyBlock = () => {
    if (emptyKind === 'unknown') return (
      <div data-testid="wa-empty-unknown" className="text-center py-12 px-6">
        <p className="text-sm font-semibold text-text-strong">{t('waGrid.empty.unknownTitle')}</p>
        <p className="text-xs text-text-muted mt-1">{t('waGrid.empty.unknownBody')}</p>
        <button type="button" data-testid="wa-connection-retry" className="mt-3 min-h-[44px] px-4 text-sm font-semibold text-primary underline" onClick={() => { void fetchConnection(); void fetchRows(); }}>
          {t('waGrid.empty.unknownRetry')}
        </button>
      </div>
    );
    if (emptyKind === 'not-connected') return (
      <div data-testid="wa-empty-not-connected" className="text-center py-12 px-6">
        <p className="text-sm font-semibold text-text-strong">{t('waGrid.empty.notConnectedTitle')}</p>
        <p className="text-xs text-text-muted mt-1">{t('waGrid.empty.notConnectedBody')}</p>
        <button type="button" data-testid="wa-connect-link" className="mt-3 min-h-[44px] px-4 text-sm font-semibold text-primary underline" onClick={onConnectWhatsApp}>
          {t('waGrid.empty.notConnectedAction')}
        </button>
      </div>
    );
    if (emptyKind === 'nothing-yet') return (
      <div data-testid="wa-empty-nothing-yet" className="text-center py-12 px-6">
        <p className="text-sm font-semibold text-text-strong">{t('waGrid.empty.nothingYetTitle')}</p>
        <p className="text-xs text-text-muted mt-1">{t('waGrid.empty.nothingYetBody')}</p>
      </div>
    );
    if (emptyKind === 'caught-up') return (
      <div data-testid="wa-empty-caught-up" className="text-center py-12 px-6">
        <p className="text-sm font-semibold text-opt-emerald">{t(activeView.emptyTitleKey ?? 'waGrid.empty.caughtUpTitle')}</p>
        <p className="text-xs text-text-muted mt-1">{t(activeView.emptyBodyKey ?? 'waGrid.empty.caughtUpFallbackBody')}</p>
      </div>
    );
    return (
      <div data-testid="wa-empty-filtered" className="text-center py-12 px-6">
        <p className="text-sm font-semibold text-text-strong">{t('waGrid.empty.filteredTitle')}</p>
        <p className="text-xs text-text-muted mt-1">{t('waGrid.empty.filteredBody')}</p>
        <button type="button" data-testid="wa-clear-filters" className="mt-3 min-h-[44px] px-4 text-sm font-semibold text-primary underline" onClick={clearFilters}>
          {t('waGrid.empty.filteredAction')}
        </button>
      </div>
    );
  };

  const body = () => {
    if (error && !loaded) return (
      <div data-testid="wa-error" className="text-center py-12 px-6">
        <p className="text-sm font-semibold text-opt-rose">{t('waGrid.error.title')}</p>
        <p className="text-xs text-text-muted mt-1">{t('waGrid.error.body')}</p>
        <button type="button" data-testid="wa-retry" className="mt-3 min-h-[44px] px-4 text-sm font-semibold text-primary underline" onClick={() => { setError(false); void fetchRows(); void fetchConnection(); }}>
          {t('waGrid.error.retry')}
        </button>
      </div>
    );
    if (!loaded) return skeletons;
    if (rows.length === 0) {
      // 🔴 AC-W14b first-paint guard: `resolving` is not an answer. Rendering an empty state before the probe
      // returns would flash "you have never had a conversation" at a not-connected account on EVERY load —
      // and a retrying assertion could never catch it, because it waits the flash out. Rows are never held
      // back; only the CLAIM about why the list is empty waits until it can be true.
      if (conn === 'resolving') return skeletons;
      return emptyBlock();
    }
    if (!isDesktop || compact) return (
      <div className={cardGridCls(compact)}>
        {rows.map((r) => <WaCardRow key={r.key} row={r} nowMs={nowMs} selected={r.key === selectedKey} onOpen={onOpen} />)}
      </div>
    );
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-start border-collapse">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-text-muted border-b border-border-neutral">
              <th className="sticky z-10 bg-surface p-4 text-start [inset-inline-start:0]">{t('waGrid.columns.contact')}</th>
              <th className="p-4 text-start">{t('waGrid.columns.phone')}</th>
              <th className="p-4 text-start">{t('waGrid.columns.lastMessage')}</th>
              <th className="p-4 text-start">{t('waGrid.columns.estado')}</th>
              <th className="p-4 text-start">{t('waGrid.columns.owner')}</th>
              <th className="p-4 text-start">{t('waGrid.columns.wait')}</th>
              <th className="p-4 text-start">{t('waGrid.columns.window')}</th>
              <th className="p-4 text-start">{t('waGrid.columns.operational')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => <WaGridRow key={r.key} row={r} nowMs={nowMs} selected={r.key === selectedKey} onOpen={onOpen} />)}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="relative w-full h-full flex flex-col" data-testid="wa-grid" data-wa-connection={conn} data-wa-connection-failures={connFailures} data-wa-pending={pendingRows ? 'yes' : 'no'} data-wa-poll-failures={consecutiveFailures} data-wa-compact={compact ? 'on' : 'off'} data-wa-view={view} data-wa-channel={channel} data-wa-filters={filtersActive ? 'on' : 'off'}>
      {header}
      <div ref={scrollBoxRef} className="flex-1 overflow-y-auto">{body()}</div>
      {statusOverlay}
    </div>
  );
};
